import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { ChampionshipSettings } from 'modules/tournaments/domain/interfaces/championship-settings';
import { RootState } from 'store/store';
import { GameDetails, GameDetailsScore } from '../domain/interfaces/game-details';
import { GameEventDto } from '../domain/interfaces/game-event-dto';
import { GamePeriodsSettings, getPeriodsSettings } from '../domain/mappers/match-periods-settings';
import gameRepository from '../domain/repositories/game-repository';

const PREFIX = 'GAMES';

export const fetchGameDetails = createAsyncThunk<GameDetails | null, { gameId: number; championshipId: number }>(
  `${PREFIX}/fetchDetails`,
  async (payload, { getState }) => {
    try {
      const state = getState() as RootState;
      const settings = state.tourneys.championship.data?.byId[payload.championshipId]?.settings || ({} as ChampionshipSettings);
      const periodsSettings = getPeriodsSettings(settings);
      return await gameRepository.fetchGameDetails(payload.gameId, periodsSettings);
    } catch (err) {
      console.error(`${PREFIX}/fetchDetails error:`, err);
      throw err;
    }
  },
);

export type ModifyGameEventData = {
  data: GameEventDto;
  periodSettings: GamePeriodsSettings;
};

export const addGameEvent = createAction<ModifyGameEventData>(`${PREFIX}/addGameEvent`);
export const editGameEvent = createAction<ModifyGameEventData>(`${PREFIX}/editGameEvent`);
export const deleteGameEvent = createAction<number>(`${PREFIX}/deleteGameEvent`);
export const editGameScore = createAction<GameDetailsScore>(`${PREFIX}/editGameScore`);

export const changeGameDetailsVideoUrl = createAction<string>(`${PREFIX}/changeGameDetailsVideoUrl`);

export const saveGameDetails = createAsyncThunk<GameDetails, GameDetails>(
  `${PREFIX}/saveGameDetails`,
  async (payload, { getState }) => {
    try {
      const state = getState() as RootState;
      const settings = state.tourneys.championship.data?.byId[payload.championship.id]?.settings || ({} as ChampionshipSettings);
      const periodsSettings = getPeriodsSettings(settings);

      const eventsPlayerIds = payload.events
        .reduce<number[]>((results, event) => [...results, ...event.playerIds], [])
        .filter(Boolean);

      const oldParticipantIds = payload.participants;
      const newParticipantIds = Array.from(new Set([...oldParticipantIds, ...eventsPlayerIds]));
      const participantsChanged = newParticipantIds.some((id) => !oldParticipantIds.includes(id));

      if (participantsChanged) {
        await gameRepository.updateMatchParticipants(payload.id, newParticipantIds);
      }

      return await gameRepository.updateGameDetails(payload, periodsSettings);
    } catch (err) {
      console.error(`${PREFIX}/saveGameDetails error:`, err);
      throw err;
    }
  },
);
